import {FC, useState, useEffect} from "react";
import {
    Brain,
    Puzzle,
    Target,
    Sparkles,
    ArrowRight,
    ChevronDown,
    ChevronUp
} from "lucide-react";

const WorkflowGuide: FC = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Mobile Layout - Completely separate
    if (isMobile) {
        return (
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-200 shadow-lg mb-8">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 rounded-full blur-2xl animate-pulse"></div>
                </div>

                <div className="relative p-4">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-3 shadow-lg">
                            <Brain className="w-6 h-6 text-white"/>
                        </div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                            Understanding Shreds
                        </h2>
                        <p className="text-gray-600 text-sm max-w-3xl mx-auto leading-relaxed">
                            Shreds are modular components that make up your service. Think of them as intelligent building
                            blocks that work together to create a complete, scalable application.
                        </p>
                    </div>

                    {/* Show More Button for Mobile */}
                    <div className="text-center mb-4">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-md"
                        >
                            <span>{isExpanded ? 'Show Less' : 'Show More Details'}</span>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                    </div>

                    {/* Mobile Detailed Content */}
                    {isExpanded && (
                        <div className="space-y-4">
                            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-indigo-100">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-3">
                                    <Puzzle className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-base font-semibold text-gray-800 mb-2">What is a Shred?</h3>
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    A Shred is a module representing a feature or set of related features in your service. Each
                                    shred contains its own logic, data models, and functionality.
                                </p>
                            </div>

                            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-indigo-100">
                                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mb-3">
                                    <Target className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-base font-semibold text-gray-800 mb-2">Why Use Shreds?</h3>
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    Dividing your service into smaller shreds makes it easier to manage, maintain, and scale.
                                    You can generate them faster and parallelize development work.
                                </p>
                            </div>

                            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-indigo-100">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-3">
                                    <Sparkles className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-base font-semibold text-gray-800 mb-2">How to Generate?</h3>
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    Use AI to automatically generate shreds, or create them manually. Start with the data model,
                                    then add feature shreds for complete functionality.
                                </p>
                            </div>

                            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl p-4 border border-indigo-200">
                                <h4 className="text-base font-semibold text-center text-gray-800 mb-4">Recommended Workflow</h4>
                                <div className="flex flex-col items-center justify-center space-y-3">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xs">1</div>
                                        <span className="text-gray-700 font-medium text-sm">Generate Shreds List</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-7 h-7 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-xs">2</div>
                                        <span className="text-gray-700 font-medium text-sm">Generate Specs</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-7 h-7 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-xs">3</div>
                                        <span className="text-gray-700 font-medium text-sm">Explore & Generate Code</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-7 h-7 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold text-xs">4</div>
                                        <span className="text-gray-700 font-medium text-sm">Assemble Shreds to a Service</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Desktop Layout - EXACTLY as it was in main branch (copy-pasted without any changes)
    return (
        <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-200 shadow-lg mb-8">
            <div className="absolute inset-0 overflow-hidden">
                <div
                    className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 rounded-full blur-2xl animate-pulse"></div>
            </div>

            <div className="relative p-8">
                <div className="text-center mb-8">
                    <div
                        className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
                        <Brain className="w-8 h-8 text-white"/>
                    </div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3">
                        Understanding Shreds
                    </h2>
                    <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
                        Shreds are modular components that make up your service. Think of them as intelligent building
                        blocks that work together to create a complete, scalable application.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                        <div
                            className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                            <Puzzle className="w-6 h-6 text-white"/>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">What is a Shred?</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            A Shred is a module representing a feature or set of related features in your service. Each
                            shred contains its own logic, data models, and functionality.
                        </p>
                    </div>

                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                        <div
                            className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mb-4">
                            <Target className="w-6 h-6 text-white"/>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Why Use Shreds?</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Dividing your service into smaller shreds makes it easier to manage, maintain, and scale.
                            You can generate them faster and parallelize development work.
                        </p>
                    </div>

                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                        <div
                            className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                            <Sparkles className="w-6 h-6 text-white"/>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Generate?</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Use AI to automatically generate shreds, or create them manually. Start with the data model,
                            then add feature shreds for complete functionality.
                        </p>
                    </div>
                </div>

                <div
                    className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-200">
                    <h4 className="text-lg font-semibold text-center text-gray-800 mb-6">Recommended Workflow</h4>
                    <div
                        className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1
                            </div>
                            <span className="text-gray-700 font-medium">Generate Shreds List</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 hidden md:block"/>
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2
                            </div>
                            <span className="text-gray-700 font-medium">Generate Specs</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 hidden md:block"/>
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3
                            </div>
                            <span className="text-gray-700 font-medium">Explore & Generate Code</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 hidden md:block"/>
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4
                            </div>
                            <span className="text-gray-700 font-medium">Assemble Shreds to a Service</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkflowGuide;